import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ServerStackIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import Layout from '../../components/layout/Layout';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Select,
  SelectOption,
  Spinner,
  TabularDataViewer,
  type TabularColumn,
} from '../../components/ui';
import { cn } from '../../shared/lib/cn';
import { useToasts } from '../../stores/useToasts';
import {
  useAPCutoffAvailableConnectors,
  useAPCutoffDataSourceConfig,
  useAPCutoffRun,
  useClearAPCutoffDataSource,
  useSetAPCutoffDataSource,
  useTestAPCutoffConnection,
} from './api';
import type {
  APCutoffTab,
  APException,
  APInvoice,
  APJournalEntryRow,
  APReceipt,
  APRun,
} from './types';
import {
  apCutoffTabToPath,
  buildNetSuiteCsv,
  formatCurrency,
  routeToAPCutoffTab,
} from './utils';

const PERIODS = ['Apr 2025'];

const TAB_LABELS: Array<{ id: APCutoffTab; label: string }> = [
  { id: 'review', label: 'Controller Review' },
  { id: 'exceptions', label: 'Exceptions' },
  { id: 'journal-entry', label: 'Journal Entry' },
  { id: 'source-data', label: 'Source Data' },
  { id: 'configuration', label: 'Configuration' },
];

export default function APCutoffEnforcerPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const pushToast = useToasts(state => state.push);
  const activeTab = routeToAPCutoffTab(location.pathname);
  const [period, setPeriod] = useState('Apr 2025');
  const { data, isLoading, isError, refetch } = useAPCutoffRun(period);

  const selectTab = (tab: APCutoffTab) => {
    navigate(apCutoffTabToPath(tab));
  };

  const exportJournal = () => {
    if (!data) return;
    const csv = buildNetSuiteCsv(data.journal_entries);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `EC05_AP_Cutoff_${data.kpis.period.replace(/\s+/g, '')}.csv`;
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(link.href);
    link.remove();
    pushToast({ kind: 'success', message: 'NetSuite AP accrual CSV downloaded.' });
  };

  return (
    <Layout>
      <div className="flex h-full flex-col bg-gray-50 dark:bg-dark-bg">
        <div
          className="border-b border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface"
          data-toolbar-surface="solid"
        >
          <div className="mx-auto max-w-[1680px] px-6 py-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <Badge variant={data?.kpis.critical_count ? 'warning' : 'success'}>
                  EC-05
                </Badge>
                {data?.data_source && (
                  <Badge variant={data.data_source.is_connected ? 'success' : 'secondary'}>
                    {data.data_source.mode}
                  </Badge>
                )}
                {data?.agent_review.traced_with_langfuse && (
                  <Badge variant="secondary">Langfuse traced</Badge>
                )}
              </div>
              <h1 className="mt-2 font-title text-h1 text-charcoal dark:text-gray-100">
                AP Cut-Off Enforcer
              </h1>
              <p className="mt-1 max-w-3xl text-sm text-gray-500 dark:text-gray-400">
                Late invoice detection, receiving tie-out, proposed AP accrual, and Controller approval workflow.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={period} onValueChange={setPeriod} className="w-40">
                {PERIODS.map(option => (
                  <SelectOption key={option} value={option}>
                    {option}
                  </SelectOption>
                ))}
              </Select>
              <Button variant="secondary" onClick={() => refetch()}>
                <ArrowPathIcon className="h-4 w-4" />
                Refresh
              </Button>
              <Button onClick={exportJournal} disabled={!data?.journal_entries.length}>
                <ArrowDownTrayIcon className="h-4 w-4" />
                Export JE
              </Button>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {TAB_LABELS.map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => selectTab(tab.id)}
                className={cn(
                  'rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  activeTab === tab.id
                    ? 'bg-eliza-red text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-dark-surface-2 dark:text-gray-300 dark:hover:bg-dark-border'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
          </div>
        </div>

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[1680px] p-6">
          {isLoading && (
            <div className="flex h-64 items-center justify-center">
              <Spinner size="lg" />
            </div>
          )}

          {isError && (
            <Card>
              <CardContent className="flex items-center gap-3 p-6 text-red-600">
                <XCircleIcon className="h-5 w-5" />
                Unable to load AP cut-off run.
              </CardContent>
            </Card>
          )}

          {data && activeTab === 'review' && <ReviewTab data={data} />}
          {data && activeTab === 'exceptions' && <ExceptionsTab rows={data.exceptions} />}
          {data && activeTab === 'journal-entry' && <JournalTab rows={data.journal_entries} />}
          {data && activeTab === 'source-data' && (
            <SourceDataTab invoices={data.invoices} receipts={data.receipts} />
          )}
          {activeTab === 'configuration' && <ConfigurationTab />}
          </div>
        </main>
      </div>
    </Layout>
  );
}

function ReviewTab({ data }: { data: APRun }) {
  const kpis = data.kpis;
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Proposed Accrual"
          value={formatCurrency(kpis.proposed_accrual)}
          icon={<DocumentTextIcon className="h-5 w-5" />}
        />
        <MetricCard
          label="Exceptions"
          value={`${kpis.exception_count}`}
          detail={`${kpis.critical_count} critical`}
          icon={<ExclamationTriangleIcon className="h-5 w-5" />}
        />
        <MetricCard
          label="Invoices Scanned"
          value={`${kpis.invoice_count}`}
          detail={`${kpis.receipt_count} receipts`}
          icon={<ClipboardDocumentListIcon className="h-5 w-5" />}
        />
        <MetricCard
          label="JE Balance"
          value={formatCurrency(Math.abs(kpis.balance_delta))}
          detail="Debit and credit delta"
          icon={<CheckCircleIcon className="h-5 w-5" />}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <Card>
          <CardContent className="p-5">
            <h2 className="font-subtitle text-h3 text-charcoal dark:text-gray-100">
              Agent Review
            </h2>
            <div className="mt-4 space-y-4">
              <ReviewList title="Observations" items={data.agent_review.observations} />
              <ReviewList title="Risks" items={data.agent_review.risks} />
              <ReviewList title="Recommended Actions" items={data.agent_review.recommended_actions} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <h2 className="font-subtitle text-h3 text-charcoal dark:text-gray-100">
              Cut-Off Policy
            </h2>
            <dl className="mt-4 space-y-3 text-sm">
              <Row label="Close period" value={data.policy.period} />
              <Row label="Cut-off date" value={data.policy.cutoff_date} />
              <Row label="Materiality" value={formatCurrency(data.policy.materiality_threshold)} />
              <Row
                label="Accrual account"
                value={`${data.policy.accrual_account} ${data.policy.accrual_account_name}`}
              />
              <Row label="Prompt backend" value={data.agent_review.prompt_source_backend} />
            </dl>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ExceptionsTab({ rows }: { rows: APException[] }) {
  const columns = useMemo<TabularColumn<APException>[]>(
    () => [
      { id: 'status', header: 'Status', accessorKey: 'status' },
      { id: 'source_id', header: 'Source', accessorKey: 'source_id' },
      { id: 'vendor', header: 'Vendor', accessorKey: 'vendor' },
      { id: 'department', header: 'Department', accessorKey: 'department' },
      { id: 'amount', header: 'Amount', accessorKey: 'amount', type: 'currency' },
      { id: 'proposed_accrual', header: 'Proposed Accrual', accessorKey: 'proposed_accrual', type: 'currency' },
      { id: 'reason', header: 'Reason', accessorKey: 'reason' },
    ],
    []
  );
  return (
    <TabularDataViewer
      title="Cut-Off Exceptions"
      description="Invoices and receiving records requiring Controller attention."
      data={rows}
      columns={columns}
      getRowId={row => row.id}
      exportFilename="ap-cutoff-exceptions.csv"
      maxHeight="640px"
    />
  );
}

function JournalTab({ rows }: { rows: APJournalEntryRow[] }) {
  const columns = useMemo<TabularColumn<APJournalEntryRow>[]>(
    () => [
      { id: 'line', header: 'Line', accessorKey: 'line' },
      { id: 'account', header: 'Account', accessorKey: 'account' },
      { id: 'account_name', header: 'Account Name', accessorKey: 'account_name' },
      { id: 'department', header: 'Department', accessorKey: 'department' },
      { id: 'debit', header: 'Debit', accessorKey: 'debit', type: 'currency' },
      { id: 'credit', header: 'Credit', accessorKey: 'credit', type: 'currency' },
      { id: 'line_memo', header: 'Memo', accessorKey: 'line_memo' },
    ],
    []
  );
  return (
    <TabularDataViewer
      title="Proposed NetSuite Journal Entry"
      description="Balanced AP accrual generated from material cut-off exceptions."
      data={rows}
      columns={columns}
      getRowId={row => row.id}
      exportFilename="ap-cutoff-journal-entry.csv"
      maxHeight="640px"
    />
  );
}

function SourceDataTab({ invoices, receipts }: { invoices: APInvoice[]; receipts: APReceipt[] }) {
  const invoiceColumns = useMemo<TabularColumn<APInvoice>[]>(
    () => [
      { id: 'invoice_id', header: 'Invoice', accessorKey: 'invoice_id' },
      { id: 'vendor', header: 'Vendor', accessorKey: 'vendor' },
      { id: 'posting_period', header: 'Posting Period', accessorKey: 'posting_period' },
      { id: 'service_end_date', header: 'Service End', accessorKey: 'service_end_date' },
      { id: 'amount', header: 'Amount', accessorKey: 'amount', type: 'currency' },
      { id: 'department', header: 'Department', accessorKey: 'department' },
    ],
    []
  );
  const receiptColumns = useMemo<TabularColumn<APReceipt>[]>(
    () => [
      { id: 'receipt_id', header: 'Receipt', accessorKey: 'receipt_id' },
      { id: 'po_number', header: 'PO', accessorKey: 'po_number' },
      { id: 'vendor', header: 'Vendor', accessorKey: 'vendor' },
      { id: 'receipt_date', header: 'Receipt Date', accessorKey: 'receipt_date' },
      { id: 'amount', header: 'Amount', accessorKey: 'amount', type: 'currency' },
      { id: 'matched_invoice_id', header: 'Matched Invoice', accessorKey: 'matched_invoice_id' },
    ],
    []
  );

  return (
    <div className="space-y-6">
      <TabularDataViewer
        title="AP Invoices"
        data={invoices}
        columns={invoiceColumns}
        getRowId={row => row.invoice_id}
        exportFilename="ap-cutoff-invoices.csv"
        maxHeight="420px"
      />
      <TabularDataViewer
        title="Receiving Evidence"
        data={receipts}
        columns={receiptColumns}
        getRowId={row => row.receipt_id}
        exportFilename="ap-cutoff-receipts.csv"
        maxHeight="420px"
      />
    </div>
  );
}

function ConfigurationTab() {
  const pushToast = useToasts(state => state.push);
  const { data: config, isLoading: configLoading } = useAPCutoffDataSourceConfig();
  const { data: connectors, isLoading: connectorsLoading } = useAPCutoffAvailableConnectors();
  const setDataSource = useSetAPCutoffDataSource();
  const clearDataSource = useClearAPCutoffDataSource();
  const testConnection = useTestAPCutoffConnection();
  const [connectorId, setConnectorId] = useState('');
  const [schema, setSchema] = useState('ap_cutoff_enforcer');

  useEffect(() => {
    if (!config) return;
    setConnectorId(config.connector_id ?? '');
    setSchema(config.redshift_schema ?? 'ap_cutoff_enforcer');
  }, [config]);

  const save = () => {
    if (!connectorId) {
      pushToast({ kind: 'error', message: 'Select a Redshift connector.' });
      return;
    }
    setDataSource.mutate(
      { connector_id: connectorId, redshift_schema: schema || 'ap_cutoff_enforcer' },
      {
        onSuccess: () => pushToast({ kind: 'success', message: 'AP cut-off data source saved.' }),
        onError: error => pushToast({ kind: 'error', message: error.message }),
      }
    );
  };

  const test = () => {
    if (!connectorId) return;
    testConnection.mutate(
      { connector_id: connectorId, redshift_schema: schema || 'ap_cutoff_enforcer' },
      {
        onSuccess: result =>
          pushToast({
            kind: result.redshift_connected ? 'success' : 'warning',
            message: result.message || 'Connection test completed.',
          }),
        onError: error => pushToast({ kind: 'error', message: error.message }),
      }
    );
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <ServerStackIcon className="h-5 w-5 text-gray-500" />
            <h2 className="font-subtitle text-h3 text-charcoal dark:text-gray-100">
              Redshift Data Source
            </h2>
          </div>

          {configLoading ? (
            <div className="mt-6 flex justify-center">
              <Spinner />
            </div>
          ) : (
            <dl className="mt-5 space-y-3 text-sm">
              <Row label="Mode" value={config?.mode ?? 'fixtures'} />
              <Row label="Connector" value={config?.connector_name ?? 'Not selected'} />
              <Row label="Schema" value={config?.redshift_schema ?? 'ap_cutoff_enforcer'} />
              <Row label="Host" value={config?.host ?? 'N/A'} />
            </dl>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <h2 className="font-subtitle text-h3 text-charcoal dark:text-gray-100">
            Configure Source
          </h2>
          <div className="mt-5 space-y-4">
            <div>
              <Label htmlFor="ap-cutoff-connector">Redshift connector</Label>
              <Select
                value={connectorId}
                onValueChange={setConnectorId}
                disabled={connectorsLoading}
              >
                <SelectOption value="">Select connector</SelectOption>
                {(connectors ?? []).map(connector => (
                  <SelectOption key={connector.connector_id} value={connector.connector_id}>
                    {connector.connector_name}
                  </SelectOption>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="ap-cutoff-schema">Redshift schema</Label>
              <Input
                id="ap-cutoff-schema"
                value={schema}
                onChange={event => setSchema(event.target.value)}
                placeholder="ap_cutoff_enforcer"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <Button onClick={save} disabled={setDataSource.isPending}>
                Save Source
              </Button>
              <Button variant="secondary" onClick={test} disabled={!connectorId || testConnection.isPending}>
                Test Connection
              </Button>
              <Button
                variant="ghost"
                onClick={() =>
                  clearDataSource.mutate(undefined, {
                    onSuccess: () => pushToast({ kind: 'info', message: 'AP cut-off source cleared.' }),
                  })
                }
                disabled={clearDataSource.isPending}
              >
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({
  label,
  value,
  detail,
  icon,
}: {
  label: string;
  value: string;
  detail?: string;
  icon: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
            <p className="mt-2 font-title text-2xl text-charcoal dark:text-gray-100">{value}</p>
            {detail && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{detail}</p>}
          </div>
          <div className="rounded-md bg-gray-100 p-2 text-gray-500 dark:bg-dark-surface-2 dark:text-gray-400">
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ReviewList({ title, items }: { title: string; items: string[] }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">{title}</h3>
      <ul className="mt-2 space-y-2">
        {items.length === 0 ? (
          <li className="text-sm text-gray-500 dark:text-gray-400">None.</li>
        ) : (
          items.map(item => (
            <li key={item} className="flex gap-2 text-sm text-gray-600 dark:text-gray-300">
              <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-eliza-red" />
              <span>{item}</span>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="max-w-[60%] text-right font-medium text-charcoal dark:text-gray-100">
        {value}
      </dd>
    </div>
  );
}
